"use client";

import { Component, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { createPortal } from "react-dom";
import { STATUS_META } from "@/lib/invoice/utils";

export const cx = (...c) => c.filter(Boolean).join(" ");

export function Button({ variant = "primary", size = "md", className, ...props }) {
  const v = {
    primary:
      "bg-gradient-to-b from-brand-600 to-brand-700 text-white shadow-xs ring-1 ring-inset ring-black/10 hover:from-brand-500 hover:to-brand-600 disabled:from-brand-300 disabled:to-brand-300",
    secondary: "bg-white text-slate-800 shadow-xs ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:ring-slate-300 disabled:text-slate-400",
    ghost: "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
    danger: "bg-white text-brand-700 shadow-xs ring-1 ring-inset ring-brand-200 hover:bg-brand-50",
    dangerSolid: "bg-brand-600 text-white shadow-xs hover:bg-brand-700",
    success: "bg-gradient-to-b from-emerald-500 to-emerald-600 text-white shadow-xs ring-1 ring-inset ring-black/10 hover:from-emerald-400 hover:to-emerald-500",
    dark: "bg-ink-900 text-white shadow-xs hover:bg-ink-700",
  }[variant];
  const s = { sm: "h-8 px-3 text-xs", md: "h-9 px-3.5 text-sm", lg: "h-11 px-5 text-sm" }[size];
  return (
    <button
      type="button"
      className={cx(
        "inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-1 active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100",
        v,
        s,
        className
      )}
      {...props}
    />
  );
}

export function Field({ label, hint, children, className }) {
  return (
    <label className={cx("block", className)}>
      {label && <span className="mb-1.5 block text-[12px] font-medium text-slate-600">{label}</span>}
      {children}
      {hint && <span className="mt-1 block text-[11px] leading-snug text-slate-500">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-xs transition placeholder:text-slate-400 hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-500";

export function Input({ className, ...props }) {
  return <input className={cx(inputCls, className)} {...props} />;
}

export function Textarea({ className, ...props }) {
  return <textarea className={cx(inputCls, "min-h-[84px]", className)} {...props} />;
}

export function Select({ className, options, children, ...props }) {
  return (
    <select className={cx(inputCls, "cursor-pointer pr-8", className)} {...props}>
      {options
        ? options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))
        : children}
    </select>
  );
}

export function Card({ className, children, title, subtitle, actions, flush }) {
  return (
    <section className={cx("rounded-2xl border border-slate-200/80 bg-white shadow-card", className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3.5">
          <div>
            <div className="text-[14px] font-semibold tracking-tight text-slate-900">{title}</div>
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        </header>
      )}
      <div className={flush ? "" : "p-5"}>{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions, eyebrow }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4 animate-slide-up">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-brand-600">{eyebrow}</div>}
        <h1 className="font-sans text-[26px] font-semibold leading-tight tracking-tight text-slate-900">{title}</h1>
        {subtitle && <div className="mt-1 max-w-3xl text-sm text-slate-500">{subtitle}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{actions}</div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer, wide, xwide }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  // rendered on <body> so a parent with blur/transform (e.g. the sticky top bar)
  // can't trap the dialog inside its own box
  const dialog = (
    <div className="glass-portal fixed inset-0 z-50 flex animate-fade-in items-start justify-center overflow-y-auto bg-slate-950/40 p-4 backdrop-blur-[2px] sm:p-8" onMouseDown={onClose}>
      <div
        className={cx("w-full animate-pop-in rounded-2xl bg-white shadow-pop ring-1 ring-slate-900/5", xwide ? "max-w-5xl" : wide ? "max-w-3xl" : "max-w-lg")}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="text-[15px] font-semibold tracking-tight text-slate-900">{title}</div>
          <button className="flex h-8 w-8 items-center justify-center rounded-lg text-xl leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 rounded-b-2xl border-t border-slate-100 bg-slate-50/60 px-6 py-3.5">{footer}</div>}
      </div>
    </div>
  );
  return typeof document === "undefined" ? dialog : createPortal(dialog, document.body);
}

const STATUS_DOT = {
  draft: "bg-slate-400",
  sent: "bg-blue-500",
  partial: "bg-amber-500",
  paid: "bg-emerald-500",
  overdue: "bg-red-500",
  cancelled: "bg-zinc-400",
  issued: "bg-violet-500",
  accepted: "bg-emerald-500",
  declined: "bg-zinc-400",
};

// "Aug 2026" chip for the month an invoice belongs to (colour cycles by month
// so neighbouring months are easy to tell apart at a glance).
const MONTH_TONES = [
  "bg-sky-50 text-sky-800 ring-sky-200",
  "bg-violet-50 text-violet-800 ring-violet-200",
  "bg-emerald-50 text-emerald-800 ring-emerald-200",
  "bg-amber-50 text-amber-800 ring-amber-200",
  "bg-rose-50 text-rose-800 ring-rose-200",
  "bg-teal-50 text-teal-800 ring-teal-200",
];
export function MonthTag({ date, className = "" }) {
  const m = /^(\d{4})-(\d{2})/.exec(date || "");
  if (!m) return null;
  const label = new Date(+m[1], +m[2] - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  return (
    <span className={cx("inline-flex items-center whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset", MONTH_TONES[(+m[1] * 12 + +m[2]) % MONTH_TONES.length], className)} data-testid="month-tag">
      {label}
    </span>
  );
}

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={cx("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ring-black/5", m.cls)}>
      <span className={cx("h-1.5 w-1.5 rounded-full", STATUS_DOT[status] || "bg-slate-400")} />
      {m.label}
    </span>
  );
}

export function Empty({ title, children, icon }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-14 text-center">
      <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-lg text-slate-500">{icon || "✦"}</div>
      <div className="font-semibold text-slate-800">{title}</div>
      <div className="mx-auto mt-1 max-w-md text-sm text-slate-500">{children}</div>
    </div>
  );
}

export function Stat({ label, value, sub, tone, icon, href }) {
  const body = (
    <div className="group relative h-full overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card transition hover:shadow-lift">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[12px] font-medium text-slate-500">{label}</div>
        {icon && <div className={cx("flex h-8 w-8 items-center justify-center rounded-lg", tone === "red" ? "bg-brand-50 text-brand-600" : tone === "green" ? "bg-emerald-50 text-emerald-600" : tone === "amber" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-600")}>{icon}</div>}
      </div>
      <div className={cx("mt-2 text-[24px] font-semibold tabular-nums tracking-tight", tone === "red" ? "text-brand-700" : tone === "green" ? "text-emerald-700" : "text-slate-900")}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
  return href ? <Link href={href} className="block">{body}</Link> : body;
}

export function Loading() {
  return (
    <div className="space-y-4 py-2" aria-busy="true">
      <div className="h-8 w-56 animate-pulse rounded-lg bg-slate-200/70" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-200/50" />)}
      </div>
      <div className="h-64 animate-pulse rounded-2xl bg-slate-200/40" />
    </div>
  );
}

// ---------- toasts & confirm dialogs (replace window.alert / window.confirm) ----------
const FeedbackContext = createContext(null);

export function FeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);

  const toast = useCallback((message, opts = {}) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, message, tone: opts.tone || "info", action: opts.action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), opts.duration || (opts.action ? 7000 : 3500));
  }, []);

  const confirm = useCallback(
    (message, opts = {}) =>
      new Promise((resolve) => setDialog({ message, title: opts.title || "Are you sure?", okLabel: opts.okLabel || "Confirm", danger: !!opts.danger, resolve })),
    []
  );

  const close = (v) => {
    dialog?.resolve(v);
    setDialog(null);
  };

  return (
    <FeedbackContext.Provider value={{ toast, confirm }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 left-1/2 z-[60] flex w-full max-w-md -translate-x-1/2 flex-col gap-2 px-4 print:hidden">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cx(
              "toast-surface pointer-events-auto flex animate-slide-up items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm shadow-pop ring-1 ring-black/5",
              t.tone === "error" ? "bg-red-600 text-white" : t.tone === "success" ? "bg-ink-900 text-white" : "bg-ink-900 text-white"
            )}
          >
            <span>{t.message}</span>
            {t.action && (
              <button
                className="shrink-0 font-semibold underline"
                onClick={() => {
                  t.action.onClick();
                  setToasts((all) => all.filter((x) => x.id !== t.id));
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
      <Modal
        open={!!dialog}
        onClose={() => close(false)}
        title={dialog?.title}
        footer={
          <>
            <Button variant="secondary" onClick={() => close(false)}>Cancel</Button>
            <Button variant={dialog?.danger ? "dangerSolid" : "primary"} onClick={() => close(true)} autoFocus>
              {dialog?.okLabel}
            </Button>
          </>
        }
      >
        <div className="text-sm text-slate-700">{dialog?.message}</div>
      </Modal>
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  return ctx || { toast: (m) => window.alert(m), confirm: async (m) => window.confirm(m) };
}

// ---------- image upload (logos, signatures) ----------
// Read an image file as a data URL no larger than `max` px on its longest side
// (smaller images keep their own size).
export function resizeImage(file, max = 360, type = "image/png") {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        const ctx = c.getContext("2d");
        if (type === "image/jpeg") {
          ctx.fillStyle = "#fff"; // JPEG has no transparency
          ctx.fillRect(0, 0, c.width, c.height);
        }
        ctx.drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL(type, 0.82));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// Make near-white pixels transparent (scanned stamps / signatures on white paper),
// fading the edge so the ink stays smooth.
export function removeWhiteBackground(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width;
      c.height = img.height;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const px = ctx.getImageData(0, 0, c.width, c.height);
      const d = px.data;
      for (let i = 0; i < d.length; i += 4) {
        const light = Math.min(d[i], d[i + 1], d[i + 2]);
        if (light >= 235) d[i + 3] = 0;
        else if (light > 200) d[i + 3] = Math.round((d[i + 3] * (235 - light)) / 35);
      }
      ctx.putImageData(px, 0, 0);
      resolve(c.toDataURL("image/png"));
    };
    img.src = dataUrl;
  });
}

export function ImageUpload({ value, onChange, label = "Upload", hint, wide, max, tall }) {
  const ref = useRef(null);
  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img
          src={value}
          alt=""
          className={cx("rounded border border-slate-200 object-contain", tall ? "h-20 w-20" : wide ? "h-12 w-28" : "h-14 w-14")}
          style={{ background: "repeating-conic-gradient(#f1f5f9 0% 25%, #fff 0% 50%) 50% / 10px 10px" }}
        />
      ) : (
        <div className={cx("flex items-center justify-center rounded border border-dashed border-slate-300 text-[10px] text-slate-400", tall ? "h-20 w-20" : wide ? "h-12 w-28" : "h-14 w-14")}>
          None
        </div>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) onChange(await resizeImage(f, max || (wide ? 480 : 360)));
          e.target.value = "";
        }}
      />
      <div className="flex flex-col items-start gap-1">
        <div className="flex gap-1">
          <Button size="sm" variant="secondary" onClick={() => ref.current?.click()}>{label}</Button>
          {value && <Button size="sm" variant="ghost" onClick={() => onChange("")}>Remove</Button>}
        </div>
        {hint && <span className="text-[11px] text-slate-500">{hint}</span>}
      </div>
    </div>
  );
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200">
      {tabs.map((t) => (
        <button
          key={t.value}
          onClick={() => onChange(t.value)}
          className={cx(
            "-mb-px border-b-2 px-3.5 py-2.5 text-sm font-medium transition",
            value === t.value ? "border-brand-600 text-slate-900" : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800"
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---------- searchable dropdown (opens on click, filters as you type, free text allowed) ----------
export function Combobox({ value, onChange, options = [], placeholder, className, disabled, emptyText = "No matches – keep typing to use your own", strict = false, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(null); // text typed since opening (null = show all)
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  const listRef = useRef(null);
  const opts = useMemo(() => options.map((o) => (typeof o === "string" ? { value: o, label: o } : { label: o.value, ...o })), [options]);
  const selected = opts.find((o) => o.value === value);
  const text = q ?? (selected ? selected.label : value || "");
  const filtered = useMemo(() => {
    if (!q) return opts;
    const n = q.trim().toLowerCase();
    const starts = [];
    const has = [];
    for (const o of opts) {
      const l = `${o.label} ${o.hint || ""} ${o.value}`.toLowerCase();
      if (o.label.toLowerCase().startsWith(n)) starts.push(o);
      else if (l.includes(n)) has.push(o);
    }
    return [...starts, ...has];
  }, [opts, q]);
  const shown = filtered.slice(0, 300);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (!boxRef.current?.contains(e.target)) close();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    listRef.current?.querySelector(`[data-i="${hi}"]`)?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const pick = (o) => {
    onChange(o.value);
    setQ(null);
    setOpen(false);
  };
  const close = () => {
    if (q != null) {
      const exact = opts.find((o) => o.label.toLowerCase() === q.trim().toLowerCase());
      if (exact) onChange(exact.value);
      else if (!strict) onChange(q);
    }
    setQ(null);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className={cx("relative", className)}>
      <input
        data-testid={testId}
        className={cx(inputCls, "pr-8")}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        onFocus={(e) => {
          setOpen(true);
          e.target.select();
        }}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setHi(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setHi((h) => Math.min(h + 1, shown.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter" && open) {
            e.preventDefault();
            if (shown[hi] && q) pick(shown[hi]);
            else close();
          } else if (e.key === "Escape") {
            // close just the list, not the dialog around it
            if (open) e.stopPropagation();
            setQ(null);
            setOpen(false);
          } else if (e.key === "Tab") close();
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Show options"
        disabled={disabled}
        onMouseDown={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
        className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-slate-400 hover:text-slate-700"
      >
        <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" /></svg>
      </button>
      {open && !disabled && (
        <div ref={listRef} role="listbox" className="absolute z-50 mt-1 max-h-64 w-full min-w-[220px] overflow-auto rounded-lg bg-white py-1 text-sm shadow-pop ring-1 ring-slate-900/10">
          {shown.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">{opts.length ? emptyText : "Nothing to choose from yet – type your own"}</div>}
          {shown.map((o, i) => (
            <div
              key={o.value + i}
              data-i={i}
              role="option"
              aria-selected={o.value === value}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(o);
              }}
              onMouseEnter={() => setHi(i)}
              className={cx("flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5", i === hi ? "bg-brand-50 text-brand-800" : "text-slate-800", o.value === value && "font-semibold")}
            >
              <span className="truncate">{o.label}</span>
              {o.hint && <span className="shrink-0 text-[11px] text-slate-400">{o.hint}</span>}
            </div>
          ))}
          {filtered.length > shown.length && <div className="px-3 py-1.5 text-[11px] text-slate-400">{filtered.length - shown.length} more – type to narrow down</div>}
        </div>
      )}
    </div>
  );
}

// ---------- error safety net ----------
// Shows what went wrong (and how to recover) instead of a blank "Application error" page.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    this.setState({ info });
    console.error("[Invoice app] crash:", error, info?.componentStack);
  }
  componentDidUpdate(prev) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null, info: null });
  }
  render() {
    const { error, info, copied } = this.state;
    if (!error) return this.props.children;
    const details = [
      `Page: ${typeof location !== "undefined" ? location.href : ""}`,
      `Version: ${process.env.NEXT_PUBLIC_APP_VERSION || ""} (build ${process.env.NEXT_PUBLIC_BUILD_ID || ""})`,
      `Browser: ${typeof navigator !== "undefined" ? navigator.userAgent : ""}`,
      `Error: ${error?.message || error}`,
      `Stack: ${(error?.stack || "").split("\n").slice(0, 8).join("\n")}`,
      `Component: ${(info?.componentStack || "").split("\n").slice(0, 10).join("\n")}`,
    ].join("\n");
    return (
      <div className="mx-auto my-10 max-w-2xl rounded-2xl bg-white p-6 shadow-card ring-1 ring-red-200" role="alert" data-testid="error-screen">
        <div className="text-lg font-semibold text-slate-900">Something went wrong on this page</div>
        <p className="mt-1 text-sm text-slate-600">Your data is safe – nothing was deleted. Try again, or send the details below so it can be fixed.</p>
        <pre className="mt-4 max-h-56 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-red-800 ring-1 ring-slate-200">{details}</pre>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => this.setState({ error: null, info: null })}>Try again</Button>
          <Button variant="secondary" onClick={() => location.reload()}>Reload page</Button>
          <Button
            variant="secondary"
            onClick={() => {
              navigator.clipboard?.writeText(details).then(() => this.setState({ copied: true }));
            }}
          >
            {copied ? "Copied ✓" : "Copy error details"}
          </Button>
          <a href="./" className="self-center text-sm text-slate-500 underline-offset-2 hover:underline">Go to dashboard</a>
        </div>
      </div>
    );
  }
}
